import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { Link } from 'react-router-dom';
import { TrendingDown, Shield, Settings, Award, ArrowRight, Sparkles } from 'lucide-react';
import styles from './Outcomes.module.css';

type OutcomeItem = {
    icon: string;
    kpi: string;
    title: string;
    desc: string;
    link: string;
};

const iconMap: Record<string, React.ReactNode> = {
    piggyBank: <TrendingDown size={28} />,
    shield: <Shield size={28} />,
    settings: <Settings size={28} />,
    award: <Award size={28} />,
};

const colorMap: Record<string, string> = {
    piggyBank: '#10b981', // Green
    shield: '#3b82f6',    // Blue
    settings: '#f59e0b',  // Amber
    award: '#8b5cf6',     // Purple
};

const OutcomeCard: React.FC<{ item: OutcomeItem; index: number }> = ({ item, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-30px' }}
        transition={{ duration: 0.4, delay: index * 0.1 }}
    >
        <Link to={item.link} className={styles.outcomes__card} style={{ '--accent-color': colorMap[item.icon] } as React.CSSProperties}>
            <div className={styles.outcomes__kpiBadge}>
                {item.kpi}
            </div>
            <div className={styles.outcomes__iconWrapper} style={{ color: colorMap[item.icon] }}>
                {iconMap[item.icon]}
            </div>
            <div className={styles.outcomes__content}>
                <h3 className={styles.outcomes__cardTitle}>{item.title}</h3>
                <p className={styles.outcomes__cardDesc}>{item.desc}</p>
            </div>
            <div className={styles.outcomes__arrow}>
                <ArrowRight size={18} />
            </div>
        </Link>
    </motion.div>
);

export const Outcomes: React.FC = () => {
    const { t } = useLanguage();

    return (
        <section className={styles.outcomes}>
            <div className={styles.outcomes__container}>
                <motion.div
                    className={styles.outcomes__header}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-50px' }}
                    transition={{ duration: 0.3 }}
                >
                    <span className={styles.outcomes__tag}><Sparkles size={14} />{t.outcomes.tag}</span>
                    <h2 className={styles.outcomes__title}>{t.outcomes.title}</h2>
                    <p className={styles.outcomes__subtitle}>{t.outcomes.subtitle}</p>
                </motion.div>

                <div className={styles.outcomes__grid}>
                    {t.outcomes.items.map((item, index) => (
                        <OutcomeCard key={item.title} item={item} index={index} />
                    ))}
                </div>
            </div>
        </section>
    );
};
